import '../scss/main.scss';
import '@fortawesome/fontawesome-free/webfonts/fa-solid-900.woff2';
import '@fortawesome/fontawesome-free/webfonts/fa-regular-400.woff2';
import '@fortawesome/fontawesome-free/webfonts/fa-brands-400.woff2';

import pageTemplate from '../templates/page.hbs';
import servicesListTemplate from '../templates/partials/services-list.hbs';
import differenceCardsTemplate from '../templates/partials/difference-cards.hbs';
import nl from '../lang/nl.json';
import en from '../lang/en.json';
import fr from '../lang/fr.json';

const translations = { nl, en, fr };
const fallbackLang = 'nl';
const languageLabels = { nl: 'NL', en: 'EN', fr: 'FR' };

// Icons for the difference cards (not language-dependent).
const differenceIcons = [
    'fa-graduation-cap',
    'fa-layer-group',
    'fa-universal-access',
    'fa-handshake'
];

/**
 * Get a nested value from an object by dot-separated path.
 * @param {object} obj
 * @param {string} path e.g. "hero.title"
 * @returns {*}
 */
function getNestedValue(obj, path) {
    return path.split('.').reduce((o, k) => o?.[k], obj);
}

/**
 * Resolve a translation key, falling back to the fallback language.
 * @param {string} lang
 * @param {string} key
 * @returns {string}
 */
function t(lang, key) {
    const value = getNestedValue(translations[lang], key);
    if (value) return value;
    return getNestedValue(translations[fallbackLang], key) || '';
}

/**
 * Parse [[text]] markers into <span class="gradient-text">text</span>.
 * @param {string} str
 * @returns {string}
 */
function parseGradient(str) {
    return str.replace(/\[\[(.+?)\]\]/g, '<span class="gradient-text">$1</span>');
}

/**
 * Detect preferred language from localStorage or browser locale.
 * @returns {string}
 */
function detectLanguage() {
    const saved = localStorage.getItem('lang');
    if (saved && translations[saved]) return saved;

    const locale = navigator.language || '';
    if (locale.startsWith('nl')) return 'nl';
    if (locale.startsWith('fr')) return 'fr';
    return 'en';
}

/**
 * Render the services list into #services-list.
 * @param {string} lang
 */
function renderServices(lang) {
    const items = getNestedValue(translations[lang], 'services.items');
    const fallbackItems = getNestedValue(translations[fallbackLang], 'services.items');
    const list = (items && items.length) ? items : (fallbackItems || []);
    const container = document.getElementById('services-list');
    if (!container) return;

    container.innerHTML = servicesListTemplate({ items: list });
}

/**
 * Render the difference cards into #difference-cards.
 * @param {string} lang
 */
function renderDifferenceCards(lang) {
    const cards = getNestedValue(translations[lang], 'difference.cards');
    const fallbackCards = getNestedValue(translations[fallbackLang], 'difference.cards');
    const list = (cards && cards.length) ? cards : (fallbackCards || []);
    const container = document.getElementById('difference-cards');
    if (!container) return;

    container.innerHTML = differenceCardsTemplate({ cards: list, icons: differenceIcons });
}

/**
 * Apply all translations for the given language.
 * @param {string} lang
 */
function applyTranslations(lang) {
    // Simple text elements with data-i18n attribute.
    document.querySelectorAll('[data-i18n]').forEach(el => {
        const key = el.getAttribute('data-i18n');
        const value = t(lang, key);
        if (value) {
            // Use innerHTML to support [[gradient]] markers.
            el.innerHTML = parseGradient(value);
        }
    });

    // Dynamic sections.
    renderServices(lang);
    renderDifferenceCards(lang);

    // Update HTML lang attribute.
    document.documentElement.lang = lang;

    // Update page title.
    const title = t(lang, 'meta.title');
    if (title) document.title = title;

    // Update active language button.
    document.querySelectorAll('.lang-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.lang === lang);
    });
}

/**
 * Switch to a different language and persist the choice.
 * @param {string} lang
 */
function switchLanguage(lang) {
    localStorage.setItem('lang', lang);
    applyTranslations(lang);
}

// ── Easter egg: "Artisane deluxe" ───────────────────────────────
// Triple-click on the navbar brand to reveal the secret subtitle.
// Bas Brands & Laurent David — vakmensen, ambachtslieden, artisanes deluxe.
let easterEggClicks = 0;
let easterEggTimer = null;

function handleEasterEgg() {
    easterEggClicks++;
    clearTimeout(easterEggTimer);
    easterEggTimer = setTimeout(() => { easterEggClicks = 0; }, 800);

    if (easterEggClicks >= 3) {
        easterEggClicks = 0;
        const banner = document.getElementById('easter-egg');
        if (banner) {
            banner.textContent = 'Moodle developer / Artisane deluxe \u2728';
            banner.classList.remove('d-none');
            banner.classList.add('artisane-reveal');
            // Remove after animation completes.
            setTimeout(() => {
                banner.classList.add('d-none');
                banner.classList.remove('artisane-reveal');
            }, 3200);
        }
    }
}

// ── Contact form (Web3Forms) ────────────────────────────────────
function initContactForm() {
    const form = document.getElementById('contact-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = document.getElementById('contact-submit');
        const successEl = document.getElementById('form-success');
        const errorEl = document.getElementById('form-error');
        const originalHTML = submitBtn.innerHTML;

        // Disable button + show spinner.
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>' +
            t(detectLanguage(), 'contact.form.sending');

        // Hide previous messages.
        successEl?.classList.add('d-none');
        errorEl?.classList.add('d-none');

        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(Object.fromEntries(new FormData(form)))
            });
            const data = await response.json();

            if (data.success) {
                form.classList.add('d-none');
                successEl?.classList.remove('d-none');
                form.reset();
            } else {
                errorEl?.classList.remove('d-none');
            }
        } catch {
            errorEl?.classList.remove('d-none');
        } finally {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalHTML;
        }
    });
}

// ── Initialisation ──────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
    // Render Handlebars page template into <body>.
    const app = document.getElementById('app');
    if (app) {
        app.innerHTML = pageTemplate({});
    }

    const lang = detectLanguage();

    // Build language switcher buttons.
    const switcher = document.getElementById('language-switcher');
    if (switcher) {
        Object.entries(languageLabels).forEach(([code, label]) => {
            const btn = document.createElement('button');
            btn.className = 'btn btn-sm btn-outline-secondary lang-btn';
            btn.dataset.lang = code;
            btn.textContent = label;
            btn.addEventListener('click', () => switchLanguage(code));
            switcher.appendChild(btn);
        });
    }

    // Apply translations.
    applyTranslations(lang);

    // Smooth scroll for anchor links.
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
            const target = document.querySelector(anchor.getAttribute('href'));
            if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: 'smooth' });
            }
        });
    });

    // Contact form.
    initContactForm();

    // Easter egg listener.
    const brand = document.querySelector('#nav-brand');
    if (brand) {
        brand.style.cursor = 'default';
        brand.addEventListener('click', handleEasterEgg);
    }

    // Console easter egg.
    console.log(
        '%c\uD83D\uDD28 Moodle developer / Artisane deluxe',
        'font-size: 16px; font-weight: bold; color: #2563eb;'
    );
    console.log(
        '%cBas Brands & Laurent David \u2014 Vakmensen met aandacht voor het ambacht.',
        'font-size: 12px; color: #666;'
    );
});
